import api from './api';
import { 
  CompanyResponse, 
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyFilters 
} from '../types/company';
import { ApiResponse } from '../types/api';

/**
 * Service pour la gestion des entreprises
 * ✨ SIMPLIFIÉ: Transmet TOUTES les données sans transformation
 * Les formulaires sont la source de vérité
 */
class CompanyApiService {
  private baseUrl = '/companies';

  /**
   * Crée une nouvelle entreprise
   * @param data - TOUTES les données du formulaire EnterpriseIdentificationForm
   */
  async createCompany(data: CreateCompanyRequest): Promise<CompanyResponse> {
    console.log('📤 CompanyApiService.createCompany - Envoi de TOUTES les données:', data);
    const response = await api.post<ApiResponse<CompanyResponse>>(this.baseUrl, data);
    console.log('✅ CompanyApiService.createCompany - Réponse reçue:', response.data);
    return response.data;
  }

  /**
   * Récupère une entreprise par son ID
   */
  async getCompany(companyId: string): Promise<CompanyResponse> {
    console.log(`📥 CompanyApiService.getCompany - Récupération de l'entreprise ${companyId}`);
    const response = await api.get<ApiResponse<CompanyResponse>>(`${this.baseUrl}/${companyId}`);
    return response.data;
  }

  /**
   * Met à jour une entreprise
   * @param data - TOUTES les données modifiées (partielles ou complètes)
   */
  async updateCompany(companyId: string, data: UpdateCompanyRequest): Promise<CompanyResponse> {
    console.log(`📤 CompanyApiService.updateCompany - Envoi de TOUTES les données pour ${companyId}:`, data);
    const response = await api.patch<ApiResponse<CompanyResponse>>(`${this.baseUrl}/${companyId}`, data);
    console.log('✅ CompanyApiService.updateCompany - Réponse reçue:', response.data);
    return response.data;
  }

  async deleteCompany(companyId: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${companyId}`);
  }

  async uploadLogo(companyId: string, logoFile: File): Promise<{ url: string }> {
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${companyId}/logo`, 
      logoFile,
      'logo'
    );
    return response.data;
  }

  async uploadOwnerCV(companyId: string, cvFile: File): Promise<{ url: string }> {
    const response = await api.upload<ApiResponse<{ url: string }>>(
      `${this.baseUrl}/${companyId}/owner/cv`, 
      cvFile,
      'cv'
    );
    return response.data;
  }

  async addLocation(companyId: string, locationData: {
    name: string;
    type: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${companyId}/locations`,
      locationData
    );
    return response.data;
  }

  async removeLocation(companyId: string, locationId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${companyId}/locations/${locationId}`
    );
    return response.data;
  }

  async addPartner(companyId: string, partnerData: {
    name: string;
    role: string;
    shares: number;
    email?: string;
    phone?: string;
  }): Promise<{ id: string; message: string }> {
    const response = await api.post<ApiResponse<{ id: string; message: string }>>(
      `${this.baseUrl}/${companyId}/partners`,
      partnerData
    );
    return response.data;
  }

  async removePartner(companyId: string, partnerId: string): Promise<{ message: string }> {
    const response = await api.delete<ApiResponse<{ message: string }>>(
      `${this.baseUrl}/${companyId}/partners/${partnerId}`
    );
    return response.data;
  }

  async listCompanies(filters?: CompanyFilters): Promise<{
    companies: CompanyResponse[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    
    const response = await api.get<ApiResponse<{
      companies: CompanyResponse[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    }>>(`${this.baseUrl}?${params.toString()}`);
    
    return response.data;
  }
}

export const companyApiService = new CompanyApiService();
export default companyApiService;